import React, { useContext } from 'react';
import { AppContext } from 'context/app-context';
import { dayjs } from 'helpers/dayjs';
import { ChevronIcon, KeyIcon, QrTinyIcon } from 'assets/icons';
import { ReactComponent as Logo } from 'assets/images/logo.svg';
import {
  classnames,
  isLockedVault,
  isOwner,
  isRestrictedVault,
  showTextEllipsis,
} from 'helpers/utils';
import { isEmpty } from 'lodash';
import { Button, ButtonStyle } from 'common/button';
import CypressIds from 'cypressIds';
import { VaultStorage } from '../vault-storage';
import styles from './vault-card-large.module.scss';

type VaultCardLargeProps = {
  vault: VaultType;
  showVaultStatusLabel: Function;
  notifyClickCard: (id: number, keyEvent?: boolean) => () => void;
  notifyClickQrIcon: (id: number) => void;
  cardRef?: React.RefObject<HTMLButtonElement | null>;
};

const VaultCardLarge: React.FC<VaultCardLargeProps> = ({
  vault,
  showVaultStatusLabel,
  notifyClickCard,
  notifyClickQrIcon,
  cardRef,
}) => {
  const { state } = useContext(AppContext);
  const { user } = state.data;

  const showDefaultPicture = () => (
    <div className={styles.defaultPicture}>
      <Logo className={styles.logo} />
      <p className="text__heading4__white">
        {showTextEllipsis(vault.title, 20)}
      </p>
      <p className="text__heading6__white">A life story</p>
    </div>
  );

  const showOwnerIcon = () => <KeyIcon className={styles.keyIcon} />;

  const showCard = () => (
    <button
      type="button"
      className={styles.cardLarge}
      onClick={notifyClickCard(vault.id)}
      onKeyDown={notifyClickCard(vault.id, true)}
      data-cy={CypressIds.vaultCard}
      ref={cardRef}
    >
      <div
        className={classnames(styles.pictureBox, {
          [styles.greyImage]: isRestrictedVault(vault) || isLockedVault(vault),
        })}
      >
        {showVaultStatusLabel(vault, styles.statusLabelPosition)}

        {vault.coverUrl ? (
          <img src={vault.coverUrl} className={styles.picture} alt="logo" />
        ) : (
          showDefaultPicture()
        )}
      </div>
      <div className={styles.info}>
        <div className={styles.titleAndOwner}>
          <div className="text__body__semi__bold__medium__gray800">
            {showTextEllipsis(vault.title, 45)}
          </div>
          <div className={styles.cardIcons}>
            {!isEmpty(vault.qrCodes) && (
              <Button
                className={styles.qrIconButton}
                buttonStyle={ButtonStyle.PrimaryGhost}
                onClick={(event) => {
                  event.stopPropagation();
                  notifyClickQrIcon(vault.id);
                }}
              >
                <QrTinyIcon className={styles.qrIcon} />
              </Button>
            )}
            {isOwner(user, vault) && showOwnerIcon()}
          </div>
        </div>
        <div className={styles.lastUpdateAndCompany}>
          <div className="text__body__regular__medium__gray700">{`Last updated: ${dayjs(
            vault.lastUpdate,
          ).fromNow()}`}</div>
          <div
            className={classnames('text__body__regular__medium__gray500', {
              invisible: !vault.owner.companyName,
            })}
          >{`Powered By: ${showTextEllipsis(
            vault.owner.companyName ?? '',
            30,
          )}`}</div>
        </div>
        <VaultStorage vault={vault} className={styles.storage} />
        <div className={styles.horizontalLine} />
        <div className={styles.bottomSection}>
          <Button className={styles.openVaultButton}>
            Open <ChevronIcon />
          </Button>
        </div>
      </div>
    </button>
  );

  return showCard();
};

export { VaultCardLarge };
